import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { setAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { UserPlus, Eye, EyeOff } from 'lucide-react';
import { maskCPF, maskPhone, unmask, isValidCPF, isValidPhone } from '@/lib/masks';

export default function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', cpf: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const handleCPF = (e) => setForm(prev => ({ ...prev, cpf: maskCPF(e.target.value) }));
  const handlePhone = (e) => setForm(prev => ({ ...prev, phone: maskPhone(e.target.value) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cpfDigits = unmask(form.cpf);
    const phoneDigits = unmask(form.phone);
    if (!isValidCPF(cpfDigits)) { toast.error('CPF invalido. Deve ter 11 digitos.'); return; }
    if (!isValidPhone(phoneDigits)) { toast.error('Telefone invalido. Deve ter 10 ou 11 digitos.'); return; }
    setLoading(true);
    try {
      const res = await authAPI.register({ ...form, cpf: cpfDigits, phone: phoneDigits });
      setAuth(res.data.token, res.data.user);
      toast.success('Conta criada com sucesso!');
      navigate('/client');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0F1620] items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,255,163,0.05)] to-transparent" />
        <div className="relative z-10 text-center px-12">
          <img src="/logo-recupnet.png" alt="RecupNet" className="h-16 mx-auto mb-4" />
          <p className="text-white/60 text-lg max-w-md">
            Vamos iniciar sua regularizacao com seguranca. Seus dados e documentos sao tratados com total rastreabilidade.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src="/logo-recupnet.png" alt="RecupNet" className="h-12 mx-auto" />
          </div>
          <h2 className="font-display text-2xl font-semibold mb-2">Crie sua conta</h2>
          <p className="text-white/50 text-sm mb-8">Preencha os dados abaixo para comecar.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Nome completo</label>
              <input
                type="text"
                value={form.name}
                onChange={handleChange('name')}
                placeholder="Seu nome completo"
                required
                data-testid="register-name-input"
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={handleChange('email')}
                placeholder="seu@email.com"
                required
                data-testid="register-email-input"
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1.5">CPF</label>
              <input
                type="text"
                value={form.cpf}
                onChange={handleCPF}
                placeholder="000.000.000-00"
                required
                maxLength={14}
                inputMode="numeric"
                data-testid="register-cpf-input"
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
              />
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange('password')}
                  placeholder="Crie uma senha segura"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                  className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-white/40 hover:text-white/70 transition-colors"
                  data-testid="register-toggle-password"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/70 mb-1.5">WhatsApp</label>
              <input
                type="tel"
                value={form.phone}
                onChange={handlePhone}
                placeholder="(11) 99999-9999"
                required
                maxLength={15}
                inputMode="numeric"
                data-testid="register-phone-input"
                className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[rgba(0,255,163,0.30)] focus:ring-offset-2 focus:ring-offset-[#0B0F14]"
              />
              <p className="text-xs text-white/40 mt-1">Informe um WhatsApp valido para receber atualizacoes do seu processo.</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-form-submit-button"
              className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-50"
            >
              {loading ? <span className="rn-spinner" /> : <><UserPlus size={18} /> Criar conta</>}
            </button>
          </form>

          <p className="text-center text-sm text-white/50 mt-6">
            Ja tem conta?{' '}
            <Link to="/login" className="text-[var(--rn-green)] hover:underline" data-testid="register-login-link">
              Fazer login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
